import { useForm } from 'react-hook-form'
import { useEffect } from 'react'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { TextInput, SwitchInput } from '@/components/atoms'
import { OrderSourceModel } from '@/types'
import { OrderSourceFormState, orderSourceFieldsConfig } from './orderSourceFieldsConfig'
import { useEnterSubmit } from '@/hooks/ui'
import { Spinner } from '@/components/ui/spinner'

interface OrderSourceDialogProps {
    open: boolean
    mode: 'create' | 'edit' | 'view'
    editOrderSource: OrderSourceModel.OrderSource | null
    closeDialog: () => void
    onCreate: (data: OrderSourceFormState) => Promise<void>
    onUpdate: (id: string, data: Partial<OrderSourceFormState>) => Promise<void>
    loading: boolean
}

export const OrderSourceDialog = ({
    open,
    mode,
    editOrderSource,
    closeDialog,
    onCreate,
    onUpdate,
    loading = false,
}: OrderSourceDialogProps) => {
    const {
        handleSubmit,
        reset,
        formState: { errors },
        control,
        watch,
    } = useForm<OrderSourceFormState>({
        defaultValues: {
            name: '',
            status: 'active',
        },
    })

    const formValues = watch()
    const { fields } = orderSourceFieldsConfig({ formValues })

    useEffect(() => {
        if ((mode === 'edit' || mode === 'view') && editOrderSource) {
            reset({
                name: editOrderSource.name,
                status: editOrderSource.status,
            })
        } else {
            reset({
                name: '',
                status: 'active',
            })
        }
    }, [mode, editOrderSource, reset, open])

    const onSubmit = async (values: OrderSourceFormState) => {
        if (mode === 'create') {
            await onCreate(values)
        } else if (mode === 'edit' && editOrderSource) {
            await onUpdate(editOrderSource._id, values)
        }
    }

    useEnterSubmit(handleSubmit(onSubmit), open && mode !== 'view')

    return (
        <Dialog open={open} onOpenChange={closeDialog}>
            <DialogContent size="sm">
                <DialogHeader>
                    <DialogTitle>
                        {mode === 'create' ? 'Add source' : mode === 'edit' ? 'Edit source' : 'Source details'}
                    </DialogTitle>
                    <DialogDescription>
                        {mode === 'create'
                            ? 'Add a channel your orders come from.'
                            : mode === 'edit'
                                ? 'Rename this source or change its status.'
                                : 'Details of this order source.'}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <TextInput
                        control={control}
                        name="name"
                        label="Source name"
                        placeholder="e.g. Facebook ads, physical store"
                        disabled={mode === 'view' || loading}
                        errors={errors}
                        rules={fields.name.rules}
                    />

                    <SwitchInput
                        control={control}
                        name="status"
                        label="Status"
                        description={formValues.status === 'active' ? 'Source is active' : 'Source is inactive'}
                        disabled={mode === 'view' || loading}
                        activeValue="active"
                        inactiveValue="inactive"
                        rules={fields.status.rules}
                    />
                </div>

                <DialogFooter>
                    <Button type="button" variant="outline" onClick={closeDialog} disabled={loading}>
                        {mode === 'view' ? 'Close' : 'Cancel'}
                    </Button>
                    {mode !== 'view' && (
                        <Button
                            type="button"
                            disabled={loading}
                            onClick={handleSubmit(onSubmit)}>
                            {loading && <Spinner className="size-4" />}
                            {mode === 'create' ? 'Add source' : 'Save'}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
